'use client';

import React, { useEffect, useRef, useState } from 'react';

interface Testimonial {
  name: string;
  rating: number;
  text: string;
  date: string;
}

export default function TestimonialsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const testimonials: Testimonial[] = [
    {
      name: 'Maria S.',
      rating: 5,
      text: 'Gorgis Air Conditioning and Heating did an amazing job! They were prompt, professional, and fixed our AC quickly. Highly recommend for anyone in San Diego needing HVAC service.',
      date: 'Google Review',
    },
    {
      name: 'James T.',
      rating: 5,
      text: 'Outstanding service! They came out the same day and had our furnace running perfectly. Very knowledgeable technicians and fair pricing. Will definitely use them again.',
      date: 'Google Review',
    },
    {
      name: 'Linda R.',
      rating: 5,
      text: 'Best HVAC company in San Diego! Family owned and they really care about their customers. Installed a new AC unit and the process was smooth from start to finish.',
      date: 'Google Review',
    },
    {
      name: 'Carlos M.',
      rating: 5,
      text: 'Very professional and honest. They gave me a free estimate and explained everything clearly. No hidden fees. My AC is working great now. 100% recommend!',
      date: 'Google Review',
    },
    {
      name: 'Sarah K.',
      rating: 5,
      text: 'Called them for a tune-up and they were thorough with their 38-point inspection. Found a small issue before it became a big problem. Great preventative service!',
      date: 'Google Review',
    },
    {
      name: 'David L.',
      rating: 5,
      text: 'Excellent work on our commercial HVAC system. They handled everything professionally and the system has been running perfectly ever since. Highly recommended!',
      date: 'Google Review',
    },
  ];

  return (
    <section ref={ref} className="py-20 bg-[#252525]">
      <div className="container mx-auto px-4">
        <div className="section-title pb-12">
          <h2>Testimonials</h2>
          <p>What Our Customers Say</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className={`testimonial-card rounded-xl p-6 transition-all duration-700 bg-[#1e1e1e] border border-[#3a3a3a] hover:border-[#b5973a]/50 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-3">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <svg key={j} xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#b5973a]" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              {/* Quote */}
              <p className="text-[#ddd] text-sm leading-relaxed italic mb-4">&ldquo;{t.text}&rdquo;</p>
              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold font-poppins text-sm">{t.name}</p>
                  <p className="text-[#b5973a] text-xs">{t.date}</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#b5973a]/20 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#b5973a]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA after testimonials */}
        <div
          className={`mt-12 text-center transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#aaa] mb-4 font-sans">Join hundreds of satisfied San Diego customers</p>
          <a
            href="https://book.housecallpro.com/book/Gorgis-Air-conditioning-and-heating/8d64616590bd4fe49c1a93f43f1cbfae"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#b5973a] hover:bg-[#d4aa4a] text-white font-poppins font-bold uppercase tracking-wider px-8 py-4 rounded-full text-sm transition-all duration-300 shadow-lg shadow-[#b5973a]/30"
          >
            Book Your Appointment Today
          </a>
        </div>
      </div>
    </section>
  );
}
